/**
 * Cmarkdowntext Component
 * Compact markdown renderer for titles, labels, notes, and other inline text.
 */

"use client";

import type { CSSProperties, ReactNode } from "react";
import { useMemo } from "react";
import { Cmarkdown } from "../Cmarkdown/Cmarkdown";
import { truncateMarkdownSource } from "@cabal/shared/util/markdown-cut.js";
import { getCmarkdownTextColor } from "./Cmarkdowntext-color";
import type { CmarkdowntextProps } from "./Cmarkdowntext-types";

const COMPACT_MARKDOWN_PARAGRAPH_STYLE: CSSProperties = {
  marginBottom: 0,
};

const COMPACT_MARKDOWN_WRAPPER_STYLE: CSSProperties = {
  display: "inline-flex",
  width: "auto",
  minWidth: 0,
};

// `text-overflow: ellipsis` needs an unbroken chain of shrinkable boxes: the
// wrapper, the paragraph, and the text span are each a flex item of the level
// above, and a flex item defaults to `min-width: auto` — it refuses to shrink
// below its content, so the clamp never engages. Zeroing it at all three
// levels is what makes the ellipsis appear, and `overflow`/`whiteSpace` belong
// only on the innermost box, where the text actually flows.
const ELLIPSIS_WRAPPER_STYLE: CSSProperties = {
  minWidth: 0,
  maxWidth: "100%",
};

const ELLIPSIS_PARAGRAPH_STYLE: CSSProperties = {
  minWidth: 0,
  maxWidth: "100%",
};

const ELLIPSIS_TEXT_STYLE: CSSProperties = {
  display: "block",
  minWidth: 0,
  maxWidth: "100%",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

export function Cmarkdowntext({
  content,
  limit,
  ellipsis = false,
  inline = true,
  size,
  weight,
  color,
  opacity,
  textAlign,
  className,
  style,
  paragraphStyle,
  textStyle,
}: CmarkdowntextProps): ReactNode {
  const resolvedTextColor = getCmarkdownTextColor({ color, opacity });
  const resolvedContent = limit === undefined
    ? content
    : truncateMarkdownSource({ source: content, limit });

  // `paragraphStyle`/`paragraphTextStyle` are dependencies of Cmarkdown's
  // lex-and-render memo, so a fresh object here re-lexes the whole tree on
  // every parent render. Every Cmarkdowntext on screen paid that.
  const resolvedWrapperStyle = useMemo<CSSProperties>(() => ({
    ...COMPACT_MARKDOWN_WRAPPER_STYLE,
    ...(ellipsis ? ELLIPSIS_WRAPPER_STYLE : null),
    ...style,
  }), [ellipsis, style]);

  const resolvedParagraphStyle = useMemo<CSSProperties>(() => ({
    ...COMPACT_MARKDOWN_PARAGRAPH_STYLE,
    ...(ellipsis ? ELLIPSIS_PARAGRAPH_STYLE : null),
    ...paragraphStyle,
  }), [ellipsis, paragraphStyle]);

  const resolvedParagraphTextStyle = useMemo<CSSProperties>(() => ({
    color: resolvedTextColor,
    ...(ellipsis ? ELLIPSIS_TEXT_STYLE : null),
    ...textStyle,
  }), [resolvedTextColor, ellipsis, textStyle]);

  return (
    <Cmarkdown
      content={resolvedContent}
      inline={inline}
      className={className}
      textSize={size}
      textWeight={weight}
      textAlign={textAlign}
      style={resolvedWrapperStyle}
      paragraphStyle={resolvedParagraphStyle}
      paragraphTextStyle={resolvedParagraphTextStyle}
    />
  );
}
